import { getApiBaseUrl } from '../../config/runtimeUrls';
import { StorageAdapter } from './StorageAdapter';

const storageAdapter = new StorageAdapter();

const SESSION_MESSAGE_KEY = 'admin_token_guard_message';
export const SESSION_EXPIRED_MESSAGE = 'Session expired, please sign in again';

const REAUTH_DEBOUNCE_MS = 1500;
let lastReauthAt = 0;

const TOKEN_KEYS = [
  'auth_token',
  'refresh_token',
  'auth_v2_accessToken',
  'auth_v2_refreshToken',
  'accessToken',
  'refreshToken',
  'authToken',
  'token',
];

const COOKIE_KEYS = ['refreshToken', 'refresh_token', 'auth_token', 'accessToken', 'token'];

const isLocalHost = hostname =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');

const isControlPlaneContext = () => {
  if (typeof window === 'undefined') return false;

  const hostname = String(window.location.hostname || '').toLowerCase();
  const pathname = String(window.location.pathname || '').toLowerCase();
  const port = String(window.location.port || '');

  const controlPlaneSurface = window.__appSurface === 'control-plane';
  const adminHost =
    hostname === 'admin' || hostname.startsWith('admin.') || hostname.includes('admin.localhost');
  const localAdminPort = isLocalHost(hostname) && port === '3003';
  const superAdminRoute = pathname.startsWith('/super-admin');

  return Boolean(controlPlaneSurface || adminHost || localAdminPort || superAdminRoute);
};

const decodeJwtPayload = token => {
  try {
    const rawPayload = String(token || '').split('.')[1] || '';
    if (!rawPayload) return null;
    const normalized = rawPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = window.atob(`${normalized}${padding}`);
    return JSON.parse(decoded);
  } catch (_) {
    return null;
  }
};

const getStoredAccessToken = () => {
  const tokenFromAdapter = storageAdapter.getTokens()?.accessToken;
  if (tokenFromAdapter) return tokenFromAdapter;

  for (const key of TOKEN_KEYS) {
    const value = localStorage.getItem(key);
    const token = String(value || '').trim();
    if (token && token.split('.').length >= 3) return token;
  }

  return '';
};

const validateInternalAdminToken = token => {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return { valid: false, reason: 'invalid_jwt_payload' };
  }

  if (!Object.prototype.hasOwnProperty.call(payload, 'is_internal_admin')) {
    return { valid: false, reason: 'missing_internal_admin_claim' };
  }

  if (payload.is_internal_admin !== true) {
    return { valid: false, reason: 'internal_admin_claim_not_true' };
  }

  return { valid: true, reason: 'ok' };
};

const clearTokenStorage = () => {
  try {
    storageAdapter.clear();
  } catch (_) {}

  for (const key of TOKEN_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch (_) {}
    try {
      sessionStorage.removeItem(key);
    } catch (_) {}
  }
};

const getDomainCandidates = hostname => {
  const host = String(hostname || '').toLowerCase();
  const candidates = [''];

  if (!host || isLocalHost(host)) {
    return candidates;
  }

  candidates.push(host);
  candidates.push(`.${host}`);

  const parts = host.split('.').filter(Boolean);
  if (parts.length >= 2) {
    const baseDomain = parts.slice(-2).join('.');
    candidates.push(baseDomain);
    candidates.push(`.${baseDomain}`);
  }

  return Array.from(new Set(candidates));
};

const clearAuthCookies = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const domains = getDomainCandidates(window.location.hostname);
  for (const cookieKey of COOKIE_KEYS) {
    for (const domain of domains) {
      const domainAttr = domain ? `; domain=${domain}` : '';
      document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttr}`;
      document.cookie = `${cookieKey}=; Max-Age=0; path=/${domainAttr}`;
    }
  }
};

const clearServerRefreshCookie = () => {
  const apiBase = getApiBaseUrl();
  if (!apiBase) return;

  fetch(`${apiBase}/auth/logout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({}),
  }).catch(() => {});
};

const persistSessionMessage = message => {
  try {
    sessionStorage.setItem(SESSION_MESSAGE_KEY, message);
  } catch (_) {}
};

const buildLoginRedirectPath = () =>
  `/login?sessionMessage=${encodeURIComponent(SESSION_EXPIRED_MESSAGE)}`;

export const forceAdminReauthentication = (reason = 'admin_token_guard') => {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastReauthAt < REAUTH_DEBOUNCE_MS) return;

  lastReauthAt = now;
  clearTokenStorage();
  clearAuthCookies();
  clearServerRefreshCookie();
  persistSessionMessage(SESSION_EXPIRED_MESSAGE);

  const loginPath = buildLoginRedirectPath();
  if (window.location.pathname === '/login') {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('sessionMessage')) {
      window.history.replaceState({}, '', loginPath);
    }
    return;
  }

  if (process.env.NODE_ENV !== 'production') {
    console.warn('🔐 adminTokenGuard: forcing re-authentication', { reason });
  }
  window.location.replace(loginPath);
};

const extractApiErrorCode = error => {
  const data = error?.response?.data || {};
  return data?.error?.code || data?.code || data?.errorCode || '';
};

export const isMissingInternalAdminResponse = error => {
  const status = Number(error?.response?.status || 0);
  const code = String(extractApiErrorCode(error) || '').toUpperCase();
  return status === 403 && code === 'MISSING_INTERNAL_ADMIN';
};

export const handleAdminTokenGuardResponseError = error => {
  if (!isControlPlaneContext()) return false;
  if (!isMissingInternalAdminResponse(error)) return false;

  forceAdminReauthentication('missing_internal_admin_response');
  return true;
};

export const validateAdminTokenOnAppMount = () => {
  if (!isControlPlaneContext()) return true;

  const token = getStoredAccessToken();
  if (!token) return true;

  const validation = validateInternalAdminToken(token);
  if (validation.valid) return true;

  forceAdminReauthentication(validation.reason);
  return false;
};

export const consumeAdminTokenGuardMessage = () => {
  if (typeof window === 'undefined') return '';

  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('sessionMessage');
    if (fromQuery) {
      params.delete('sessionMessage');
      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, '', nextUrl);
      return fromQuery;
    }
  } catch (_) {}

  try {
    const fromSession = sessionStorage.getItem(SESSION_MESSAGE_KEY) || '';
    if (fromSession) {
      sessionStorage.removeItem(SESSION_MESSAGE_KEY);
      return fromSession;
    }
  } catch (_) {}

  return '';
};
